import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useAuthNotice } from "../api/authNotice";
import { DEMO_BANNER_TEXT } from "../api/types";
import { refusalText } from "../api/errors";
import { useApiCommand, useHealth } from "../api/hooks";
import { dropWorkspaceQueries } from "../live/sink";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { TextField } from "../ui/TextField";
import { homePath } from "./access";
import { adoptMe, useSession } from "./auth";
import { PlainFrame } from "./PlainFrame";

/** Where to go after signing in: the address the person asked for, if the redirect kept it. */
export function addressAfterSignIn(state: unknown): string | null {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") && !from.startsWith("/sign-in") ? from : null;
}

export function SignInPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const session = useSession();
  const health = useHealth();
  const notice = useAuthNotice((state) => state.message);
  const signIn = useApiCommand("sign_in");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  if (session.status === "ready") {
    const to = session.me.must_change_password ? "/change-password" : (addressAfterSignIn(location.state) ?? homePath(session.me));
    return <Navigate to={to} replace state={location.state} />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password) return;
    setProblem(null);
    try {
      const reply = await signIn.mutateAsync({ username: username.trim(), password });
      // Nothing read for an earlier person stays on the page.
      dropWorkspaceQueries(queryClient);
      useAuthNotice.getState().clear();
      adoptMe(queryClient, reply.data);
    } catch (error) {
      setProblem(refusalText(error));
      setPassword("");
    }
  };

  const demo = health.data?.agent?.demo === true;

  return (
    <PlainFrame>
      {demo && <Banner tone="demo">{DEMO_BANNER_TEXT}</Banner>}
      <Card aria-labelledby="sign-in-title" className="sign-in">
        <h1 id="sign-in-title">Sign in</h1>
        {notice && <Banner>{notice}</Banner>}
        <form onSubmit={(event) => void submit(event)} className="stack" noValidate>
          <TextField
            label="User name"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          {problem && (
            <p role="alert" className="form-error">
              {problem}
            </p>
          )}
          <div className="row">
            <Button type="submit" variant="primary" busy={signIn.isPending} disabled={!username.trim() || !password}>
              Sign in
            </Button>
          </div>
        </form>
        <p className="muted">Your user name comes from the admin. If you have none, or forgot your password, ask the admin.</p>
      </Card>
    </PlainFrame>
  );
}
